import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Link,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { SectionCard, SectionHeader } from '../components/SettingsShell';
import {
	SMS_OPT_IN,
	SMS_PRIVACY_URL,
	SMS_TERMS_URL,
} from '../../../Shared/smsOptInCopy';

const TRIGGER_TOGGLES = [
	{ name: 'sms_on_bid_accepted', trigger: 'bid_accepted', label: 'Bid accepted' },
	{ name: 'sms_on_schedule_assigned', trigger: 'schedule_assigned', label: 'Schedule assigned' },
	{ name: 'sms_on_schedule_changed', trigger: 'schedule_changed', label: 'Schedule changed or removed' },
	{ name: 'sms_on_clock_in', trigger: 'clock_in', label: 'Clock in' },
	{ name: 'sms_on_clock_out', trigger: 'clock_out', label: 'Clock out' },
	{ name: 'sms_on_geofence_failure', trigger: 'geofence_failure', label: 'Geofence / location failure' },
	{ name: 'sms_on_bid_expired', trigger: 'bid_expired', label: 'Bid expired' },
];

const toTimeInputValue = (value) => {
	if (!value) {
		return '';
	}
	return String(value).slice(0, 5);
};

/**
 * Manager SMS alert destination + TCPA/A2P opt-in + per-trigger toggles.
 */
const SmsAlertsSection = ({
	formData,
	setFormData,
	saving,
	canUseSmsNotifications,
	savedPhone = '',
	allowedTriggers,
	onSave,
}) => {
	const [consent, setConsent] = useState(false);
	const [consentError, setConsentError] = useState('');

	const currentPhone = (formData.sms_alert_phone || '').trim();
	const priorPhone = (savedPhone || '').trim();
	const phoneChanged = currentPhone !== priorPhone;
	const needsConsent = Boolean(currentPhone) && (phoneChanged || !priorPhone);

	useEffect(() => {
		setConsent(false);
		setConsentError('');
	}, [currentPhone]);

	const visibleToggles =
		Array.isArray(allowedTriggers) && allowedTriggers.length > 0
			? TRIGGER_TOGGLES.filter((toggle) =>
					allowedTriggers.includes(toggle.trigger),
				)
			: TRIGGER_TOGGLES;

	const onChange = (event) => {
		const { name, value, type, checked } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
		if (name === 'sms_alert_phone') {
			setConsent(false);
			setConsentError('');
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		if (needsConsent && !consent) {
			setConsentError(
				'Check the consent box to enable SMS alerts for this number.',
			);
			return;
		}
		setConsentError('');
		setFormData((prev) => ({
			...prev,
			sms_alert_consent: needsConsent ? consent : false,
		}));
		// Ensure consent flag is on the payload even before React state flushes.
		const payloadConsent = needsConsent ? consent : false;
		onSave(event, { sms_alert_consent: payloadConsent });
	};

	return (
		<SectionCard>
			<SectionHeader
				icon={SmsOutlinedIcon}
				title="SMS alerts"
				description="Transactional texts to one manager phone. Not for crew messaging."
			/>

			{!canUseSmsNotifications ? (
				<Typography variant="body2" color="text.secondary">
					SMS notifications are available on the Pro plan.
				</Typography>
			) : (
				<Box component="form" onSubmit={handleSubmit} noValidate>
					<Stack spacing={{ xs: 2, sm: 2.5 }}>
						<Typography variant="body2" color="text.secondary">
							{SMS_OPT_IN.purpose}
						</Typography>
						{visibleToggles.length < TRIGGER_TOGGLES.length ? (
							<Typography variant="body2" color="text.secondary">
								Your trial includes bid accepted and bid expired texts.
								Schedule, clock, and location alerts are Pro.
							</Typography>
						) : null}
						<Typography variant="body2" color="text.secondary">
							{SMS_OPT_IN.frequency} {SMS_OPT_IN.rates}{' '}
							{SMS_OPT_IN.helpStop}
						</Typography>

						<TextField
							label="Manager alert phone"
							name="sms_alert_phone"
							value={formData.sms_alert_phone}
							onChange={onChange}
							fullWidth
							placeholder="+15551234567"
							helperText="E.164 preferred (e.g. +15551234567). Separate from company phone on documents."
							autoComplete="tel"
						/>

						{needsConsent ? (
							<>
								<FormControlLabel
									sx={{ alignItems: 'flex-start', m: 0 }}
									control={
										<Checkbox
											checked={consent}
											onChange={(e) => {
												setConsent(e.target.checked);
												setConsentError('');
											}}
											sx={{ pt: 0.25 }}
										/>
									}
									label={
										<Typography variant="body2">
											{SMS_OPT_IN.consentLabel}
										</Typography>
									}
								/>
								{consentError ? (
									<Typography variant="caption" color="error">
										{consentError}
									</Typography>
								) : null}
							</>
						) : null}

						<Typography variant="body2" color="text.secondary">
							<Link
								href={SMS_TERMS_URL}
								target="_blank"
								rel="noopener noreferrer"
							>
								Terms of Service
							</Link>
							{' · '}
							<Link
								href={SMS_PRIVACY_URL}
								target="_blank"
								rel="noopener noreferrer"
							>
								Privacy Policy
							</Link>
						</Typography>

						<FormControlLabel
							control={
								<Switch
									name="sms_quiet_hours_enabled"
									checked={Boolean(formData.sms_quiet_hours_enabled)}
									onChange={onChange}
								/>
							}
							label="Quiet hours (suppress alerts overnight; bid accepted still sends)"
						/>
						{formData.sms_quiet_hours_enabled ? (
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
								<TextField
									label="Quiet hours start"
									name="sms_quiet_hours_start"
									type="time"
									value={toTimeInputValue(formData.sms_quiet_hours_start)}
									onChange={onChange}
									fullWidth
									InputLabelProps={{ shrink: true }}
								/>
								<TextField
									label="Quiet hours end"
									name="sms_quiet_hours_end"
									type="time"
									value={toTimeInputValue(formData.sms_quiet_hours_end)}
									onChange={onChange}
									fullWidth
									InputLabelProps={{ shrink: true }}
								/>
							</Stack>
						) : null}

						<Typography variant="subtitle2" sx={{ fontWeight: 700, pt: 1 }}>
							Alert triggers
						</Typography>
						<Typography variant="body2" color="text.secondary">
							All triggers default off. Enable only what you want.
						</Typography>
						{visibleToggles.map((toggle) => (
							<FormControlLabel
								key={toggle.name}
								control={
									<Switch
										name={toggle.name}
										checked={Boolean(formData[toggle.name])}
										onChange={onChange}
									/>
								}
								label={toggle.label}
							/>
						))}
						<Box>
							<Button type="submit" variant="contained" disabled={saving}>
								{saving ? 'Saving...' : SMS_OPT_IN.submitLabel}
							</Button>
						</Box>
					</Stack>
				</Box>
			)}
		</SectionCard>
	);
};

export default SmsAlertsSection;
